// src/components/modals/DeleteGRNModal.jsx
import { useState } from 'react'
import { Button } from '../ui/Button'
import { X, Trash2, AlertTriangle, Loader2 } from 'lucide-react'
import GRNService from '../../services/grnService'

const DeleteGRNModal = ({ grn, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [confirmText, setConfirmText] = useState('')

  // Handle form submission
  const handleDelete = async () => {
    if (confirmText !== grn.grnNumber) {
      return
    }

    try {
      setLoading(true)
      console.log('🗑️ Deleting GRN:', grn._id)
      
      const response = await GRNService.deleteGRN(grn._id)
      
      if (response?.success) {
        onSuccess()
      }
    } catch (error) {
      console.error('❌ Error deleting GRN:', error)
    } finally {
      setLoading(false)
    }
  }

  // Handle background click
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const isConfirmValid = confirmText === grn.grnNumber

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={handleBackgroundClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Delete GRN</h2>
              <p className="text-sm text-gray-600">This action cannot be undone</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800">
                  Warning: This action is irreversible
                </h3>
                <p className="mt-1 text-sm text-red-700">
                  Deleting this GRN will permanently remove all associated data including:
                </p>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Received item records</li>
                  <li>Payment history</li>
                  <li>Associated transaction logs</li>
                  <li>Inventory adjustments</li>
                </ul>
              </div>
            </div>
          </div>

          {/* GRN Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-gray-900">GRN Details to be Deleted:</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">GRN Number:</span>
                <span className="font-medium text-gray-900">{grn.grnNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">PO Number:</span>
                <span className="font-medium text-gray-900">{grn.purchaseOrder?.poNumber || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Supplier:</span>
                <span className="font-medium text-gray-900">
                  {grn.purchaseOrder?.supplier?.name || grn.purchaseOrder?.warehouse?.name || 'N/A'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Amount:</span>
                <span className="font-medium text-gray-900">{formatCurrency(grn.totalReceived)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                  grn.status === 'complete' ? 'bg-green-100 text-green-800' :
                  grn.status === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {grn.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Created By:</span>
                <span className="font-medium text-gray-900">{grn.createdBy?.name || 'Unknown'}</span>
              </div>
            </div>
          </div>

          {/* Confirmation Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="font-mono font-bold text-red-600">{grn.grnNumber}</span> to confirm deletion:
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={grn.grnNumber}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500">
              This helps prevent accidental deletions
            </p>
          </div>

          {/* Additional Warnings */}
          <div className="text-xs text-gray-600 space-y-1">
            <p>⚠️ Any pending payments will be canceled</p>
            <p>⚠️ Stock adjustments may need manual correction</p>
            <p>⚠️ This action will be logged for audit purposes</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={loading || !isConfirmValid}
            className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            <span>Delete GRN</span>
          </Button>
        </div>
      </div>
    </div>
  )
}

export default DeleteGRNModal